<template>
  <div>
    <public-announcement />
    <div class="flex items-center justify-center min-h-screen p-4 overflow-hidden">
      <div class="relative z-10 w-108">
        <div class="flex items-center justify-center h-28">
          <span class="" v-if="!$store.state.isPresenterWhiteLabel">
            <a href="https://ahaslides.com">
              <aha-logo
                width="165"
                height="28"
                viewBox="0 0 9600 1602"
              />
            </a>
          </span>
        </div>

        <!-- Form -->
        <div class="p-8 shadow-panel lg:p-8 rounded-3xl bg-base-0">
          <h2 class="mb-2 text-center" v-if="!isSentEmail">
            Reset password
          </h2>
          <h2 class="mb-2 text-center" v-if="isSentEmail">
            Recovery email sent
          </h2>
          <p v-if="token">
            Please enter your new password:
          </p>
          <div class="col-12">
            <form
              class="mt-3"
              @submit.prevent="onSubmitResetPassword"
              v-if="!token && !isSentEmail"
            >
              <aha-input-group class="mb-4">
                <span class="aha-input-prepend">
                  <aha-icon name="mail" />
                </span>

                <aha-input
                  type="email"
                  class="w-full"
                  placeholder="Your email"
                  required
                  v-model="email"
                />
              </aha-input-group>

              <div class="mb-4 text-sm text-red-100" v-if="textError">
                <i class="fas fa-exclamation-triangle fa-fw" aria-hidden="true" />
                {{ textError }}
              </div>

              <div class="text-center form-group">
                <div class="pb-3 col-xs-12">
                  <vue-recaptcha
                    :sitekey="keyRecaptcha"
                    @verify="onVerify"
                    @error="onRecaptchaError"
                    size="invisible"
                    @expired="onExpired"
                    :load-recaptcha-script="true"
                    recaptcha-host="www.recaptcha.net"
                    ref="recaptcha"
                    class="auth-captcha"
                  />

                  <aha-button
                    type="submit"
                    class="w-full mb-4"
                    size="lg"
                    :submitting="submitting"
                  >
                    Reset password
                  </aha-button>
                </div>
              </div>

              <div class="mt-2 mb-0 form-group">
                <p class="text-sm text-center">
                  Already have an account?
                  <router-link
                    :to="'/pages/login'+
                      (isFromMSTeams ? '?ms_teams=true' : '')"
                    class="font-bold primary"
                  >
                    Log In
                  </router-link>
                </p>
              </div>
            </form>
            <div class="text-sm text-center" v-if="isSentEmail">
              An e-mail has been sent to
              <a class="primary" :href="'mailto:' + email">{{ email }}</a>
              with further instructions.
            </div>

            <form class="mt-3" @submit.prevent="confirmPassword" v-if="token">
              <aha-input-group class="relative mb-4">
                <span
                  class="aha-input-prepend"
                >
                  <font-awesome-icon class="text-base-80" :icon="['fas', 'lock']" />
                </span>

                <aha-input
                  :type="showPassword ? 'text' : 'password'"
                  class="w-full rounded rounded-l-none aha-input_limit-char"
                  placeholder="Your password"
                  size="md"
                  required
                  v-model="password"
                />

                <span
                  class="absolute flex items-center inset-y-2 right-3"
                  @click="showPassword = !showPassword"
                >
                  <font-awesome-icon
                    class="text-base-80"
                    :icon="!showPassword ? ['fas', 'eye'] : ['fas', 'eye-slash']"
                  />
                </span>
              </aha-input-group>

              <div class="mb-4 text-sm text-red-100" v-if="textError">
                <i class="fas fa-exclamation-triangle fa-fw" aria-hidden="true" />
                {{ textError }}
              </div>

              <div class="text-center form-group">
                <div class="pb-3 col-xs-12">
                  <aha-button
                    size="lg"
                    type="submit"
                    :submitting="submitting"
                  >
                    Change password
                  </aha-button>
                </div>
              </div>

              <div class="mt-2 mb-0 form-group">
                <p class="text-center col-sm-12">
                  Already have an account?
                  <router-link
                    :to="'/pages/login'+
                      (isFromMSTeams ? '?ms_teams=true' : '')"
                    class="ml-1 primary"
                  >
                    <b>Log In</b>
                  </router-link>
                </p>
              </div>
            </form>
          </div>
        </div>

        <div class="h-28" />
      </div>

      <back-drop />
    </div>
  </div>
</template>

<script>
// @ is an alias to /src
// import Router from "@/router";
import VueRecaptcha from 'vue-recaptcha'
import Config from '@/config'
import Util from '@/util'
import BackDrop from '@/components-v2/common/auth/BackDrop'
import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'

import { PRESENTER_FORGOTPASSWORD } from '@/constant/gaCategories'

export default {
  name: 'ForgotPassword',
  components: {
    VueRecaptcha,
    BackDrop,
    PublicAnnouncement,
  },
  data() {
    return {
      email: '',
      password: '',
      textError: '',
      submitting: false,
      token: '',
      isSentEmail: false,
      tokenRecaptcha: '',
      keyRecaptcha: Config.keyRecapcha,
      showPassword: false,
      isFromMSTeams: Util.getParamValue('ms_teams', window.location.href),
    }
  },
  created() {
    this.token = Util.getParamValue('token', window.location.href)
  },
  methods: {
    onVerify(response) {
      this.tokenRecaptcha = response
      this.resetPassword()
    },
    onSubmitResetPassword() {
      if (this.submitting) return
      this.submitting = true
      this.$refs.recaptcha.execute()
      Util.removeRecaptchaCover()
    },
    onExpired() {
      this.$refs.recaptcha.reset()
      this.tokenRecaptcha = ''
    },
    setLang(lang) {
      this.$store.dispatch('setLang', lang)
    },
    onRecaptchaError() {
      this.submitting = false
      this.textError = 'Recaptcha error. If the problem still occurs, please check your internet connection.'
    },

    resetPassword() {
      this.textError = ''

      if (!Util.validateEmail(this.email)) {
        this.submitting = false
        this.textError = 'Invalid Email.'
        this.$refs.recaptcha.reset()
        return
      }
      const payload = {
        email: this.email,
      }
      payload.tokenRecaptcha = this.tokenRecaptcha

      this.$http
        .post(Config.api.resetPassword, payload)
        .then(
          () => {
            this.submitting = false
            this.isSentEmail = true
            this.tokenRecaptcha = ''
            Util.gaTrackEvent(PRESENTER_FORGOTPASSWORD, 'Presenter Get Email > Submit > Succeeded', '')
          },
          (err) => {
            this.submitting = false
            this.$refs.recaptcha.reset()
            this.tokenRecaptcha = ''
            if (err.body.message) {
              this.textError = err.body.message
            } else {
              this.textError = err.bodyText
            }
            Util.gaTrackEvent(PRESENTER_FORGOTPASSWORD, 'Presenter Get Email > Submit > Failed', JSON.stringify(err.body))
          },
        )
      if (this.isFromMSTeams) {
        setTimeout(() => {
          window.close()
        }, 6000)
      }

      Util.gaTrackEvent(PRESENTER_FORGOTPASSWORD, 'Presenter Get Email > Submit', '')
    },

    confirmPassword() {
      this.textError = ''

      if (this.password.length < 6) {
        this.textError = 'Password needs at least 6 characters.'
        return
      }

      if (this.submitting) return
      this.submitting = true
      const payload = {
        password: this.password,
      }

      this.$http
        .post(Config.api.confirmPassword, payload, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        })
        .then(
          (res) => {
            this.submitting = false
            this.$store.dispatch('setCurrentUserAction', res.body.user)
            Util.setCookieToken(res.body.token)
            this.$router.push('/apps/presentations')
            Util.gaTrackEvent(PRESENTER_FORGOTPASSWORD, 'Presenter Confirm Password > Submit > Succeeded', '')
          },
          (err) => {
            this.submitting = false
            if (err.body.message) {
              this.textError = err.body.message
            } else {
              this.textError = err.bodyText
            }
            Util.gaTrackEvent(PRESENTER_FORGOTPASSWORD, 'Presenter Confirm Password > Submit > Failed', JSON.stringify(err.body))
          },
        )
      Util.gaTrackEvent(PRESENTER_FORGOTPASSWORD, 'Presenter Confirm Password > Submit', '')
    },
  },
}
</script>
